<script type="ts" setup>
import {
  ref, computed, toRaw, watch, onMounted,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';

import {
  ElLink, ElTabs, ElTabPane, ElIcon,
} from 'element-plus';
import { Download } from '@element-plus/icons-vue';
import AppMainContent from '@/components/AppMainContent.vue';
import ActivityTable from '@/views/activity/ActivityTable.vue';
import ActivityTableFilters from '@/views/activity/ActivityTableFilters.vue';
import SelectOrganizationWarning from '@/components/SelectOrganizationWarning.vue';
import shared from '../manage-users-and-organizations/shared';

const store = useStore();
const router = useRouter();
const isOrgAdmin = store.getters['users/isOrgAdmin'];
const route = useRoute();

const { doGetStripeAuthorizeUrl, getOrganization } = shared();

const showSelectOrganization = ref(false);
const activeTransactionTab = ref('sticker');

const selectedOrganization = computed(() => store.getters['organizations/GET_SELECTED_ORGANIZATION']);

// Lazy load PDF libraries
let jsPDF = null;
let autoTable = null;

const loadPDFLibraries = async () => {
  if (!jsPDF) {
    const jsPDFModule = await import('jspdf');
    jsPDF = jsPDFModule.default;
    await import('jspdf-autotable');
    autoTable = jsPDF.prototype.autoTable;
  }
};

const fetchActivityData = async (orgId) => {
  // Clear all transaction data
  store.commit('activityLogs/MUTATE_STICKER_TRANSACTIONS', []);
  store.commit('activityLogs/MUTATE_STRIPE_TRANSACTIONS', []);
  store.commit('activityLogs/MUTATE_BENEVITY_TRANSACTIONS', []);
  store.commit('activityLogs/MUTATE_BENEVITY_W2W_TRANSACTIONS', []);
  store.dispatch('activityLogs/SET_SELECTED_LOGS', []);

  // Fetch data for the current tab with date range
  await store.dispatch('activityLogs/FETCH_CURRENT_TAB_DATA');
};

// Watch for date changes to refetch data
watch(() => store.getters['activityLogs/GET_DATE'], () => {
  if (!showSelectOrganization.value) {
    fetchActivityData();
  }
}, { deep: true });

const updateSelectOrgWarning = () => {
  if (isOrgAdmin) {
    if ((!route.params.id || route.params.id === ':id' || route.params.id === 'ORG_ID' || route.params.id === 'undefined')
          && (!selectedOrganization.value || !selectedOrganization.value.id)) {
      showSelectOrganization.value = true;
    } else {
      showSelectOrganization.value = false;

      if (route.params.id && route.params.id !== ':id' && route.params.id !== 'ORG_ID' && route.params.id !== 'undefined') {
        fetchActivityData(route.params.id);
      } else if (selectedOrganization.value && selectedOrganization.value.id) {
        fetchActivityData(selectedOrganization.value.id);
      }
    }
  } else {
    fetchActivityData();
    showSelectOrganization.value = false;
  }
};

onMounted(() => {
  updateSelectOrgWarning();
});

watch(selectedOrganization, (newOrg, oldOrg) => {
  const newOrgId = newOrg?.id;
  const oldOrgId = oldOrg?.id;

  if (newOrgId !== oldOrgId) {
    if (isOrgAdmin && newOrgId) {
      if (route.path.includes('/activity/')) {
        const newPath = `/activity/${newOrgId}`;
        if (route.path !== newPath) {
          router.push(newPath);
        } else {
          fetchActivityData(newOrgId);
        }
      } else {
        updateSelectOrgWarning();
      }
    } else if (!isOrgAdmin) {
      fetchActivityData();
    } else {
      updateSelectOrgWarning();
    }
  }
});

watch(() => route.params.id, (newId, oldId) => {
  if (newId !== oldId && newId && newId !== ':id' && newId !== 'ORG_ID' && newId !== 'undefined') {
    fetchActivityData(newId);
  }
});

const selectedRows = computed(() => store.getters['activityLogs/GET_SELECTED_LOGS']);

const isExporting = ref(false);
const onExport = async () => {
  isExporting.value = true;

  try {
    // Load PDF libraries on demand
    await loadPDFLibraries();

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: 'A4',
    });

    const tableColumn = [
      { header: 'Transaction', dataKey: 'transactionId' },
      { header: 'Date', dataKey: 'date' },
      { header: 'User', dataKey: 'userName' },
      { header: 'Total', dataKey: 'amount' },
      { header: 'Item', dataKey: 'item' },
      { header: 'Details', dataKey: 'details' },
      { header: 'Email', dataKey: 'email' },
      { header: 'Type', dataKey: 'type' },
    ];

    const tableRows = selectedRows.value.map((row) => ({
      transactionId: row.transactionId.slice(-5),
      date: new Date(row.date).toLocaleString(),
      userName: row.userName,
      amount: row.amount.toFixed(2),
      item: row.item,
      details: row.details,
      email: row.email,
      type: row.type,
    }));

    doc.autoTable({
      columns: tableColumn,
      body: tableRows,
      styles: {
        fontSize: 10,
        overflow: 'linebreak',
        cellPadding: 5,
      },
      columnStyles: {
        transactionId: { cellWidth: 80 },
        date: { cellWidth: 100 },
        userName: { cellWidth: 70 },
        amount: { cellWidth: 50 },
        item: { cellWidth: 60 },
        details: { cellWidth: 120 },
        email: { cellWidth: 120 },
        type: { cellWidth: 70 },
      },
      margin: { top: 60 },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
    });

    const now = new Date();
    const dateTimeString = now.toISOString().slice(0, 19).replace(/:/g, '-');
    const fileName = `activity_logs_${dateTimeString}.pdf`;

    doc.save(fileName);
  } catch (error) {
    console.error('Error generating PDF:', error);
  } finally {
    isExporting.value = false;
  }
};

// Helper function to extract EIN from details text
const extractEIN = (details) => {
  if (!details || typeof details !== 'string') {
    return '';
  }

  // Look for patterns like ": 840-520851555" after colon
  // This covers various ID formats: XX-XXXXXXX, XXX-XXXXXXX, XXX-XXXXXXXXX
  const einRegex = /:\s*(\d{2,3}-\d{7,9})\b/;
  const match = details.match(einRegex);

  if (match) {
    return match[1];
  }
  return '';
};

const onExportCSV = () => {
  const headers = [
    'Transaction',
    'Date',
    'User',
    'Total',
    'Item',
    'Details',
    'Email',
    'Type',
    'Processed',
    'EIN',
  ];
  // Export filtered transactions from the current tab (applies search and date filters)
  const filteredTransactions = toRaw(store.getters['activityLogs/GET_FILTERED_TRANSACTIONS']);
  const rows = filteredTransactions;
  const csvContent = [
    headers.join(','),
    ...rows.map((row) => {
      const transId = `#${row.transactionId.slice(-5).toString()}`;
      const processedStatus = row.isProcessed !== undefined ? (row.isProcessed ? 'Yes' : 'No') : 'N/A';
      const ein = extractEIN(row.details);
      const values = [
        transId,
        new Date(row.date).toLocaleString(),
        row.userName,
        row.amount.toFixed(2),
        row.item,
        row.details,
        row.email,
        row.type,
        processedStatus,
        ein,
      ];
      return values.map((value) => `"${value}"`).join(',');
    }),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const now = new Date();
  const dateTimeString = now.toISOString().slice(0, 19).replace(/:/g, '-');
  const fileName = `activity_logs_${dateTimeString}.csv`;

  if (navigator.msSaveBlob) {
    navigator.msSaveBlob(blob, fileName);
  } else {
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  isExporting.value = false;
};

async function onBoardOnStripe() {
  const organization = await getOrganization(route.params.id);
  if (!organization.stripe_onboarded && route.params.id) {
    const stripeLink = await doGetStripeAuthorizeUrl(route.params.id);
    window.location.href = stripeLink;
  } else {
    window.location.href = 'https://connect.stripe.com/';
  }
}

// Handle transaction tab changes
const handleTransactionTabChange = (tabName) => {
  activeTransactionTab.value = tabName;
  store.dispatch('activityLogs/SET_CURRENT_TAB', tabName);
};
</script>

<template lang="pug">
app-main-content
  .c-activity-logs
    //- Header Section
    section.c-activity-logs__header
      .c-activity-logs__header-content
        h1.c-activity-logs__heading Transaction Log
        p.c-activity-logs__subtitle Monitor all financial transactions and activity
      el-button.c-activity-logs__export-btn(
        type="primary"
        @click="onExportCSV"
        :loading="isExporting"
        size="large"
      )
        el-icon.me-2
          Download
        span Export

    //- Date Range Section (moved from filters)
    section.c-activity-logs__date-section(v-if="!showSelectOrganization")
      ActivityTableFilters(:show-date-only="true")

    //- Top Navigation (moved outside card)
    section.c-activity-logs__nav-section(v-if="!showSelectOrganization")
      .c-activity-logs__nav-wrapper
        .c-activity-nav
          button.c-activity-nav-link.c-activity-nav-link--active(disabled) Transactions
          button.c-activity-nav-link(@click="$router.push('/manage-resources/todos')") Resources

        .c-activity-logs__nav-actions
          el-link(
            v-if="isOrgAdmin"
            href="https://causes.benevity.org/"
            target="_blank"
            :underline="false"
            class="c-activity-action-link"
          ) Benevity Payouts
          el-link(
            @click="onBoardOnStripe"
            :underline="false"
            class="c-activity-action-link"
          ) Stripe Account

    //- Transaction Tabs (moved outside card)
    section.c-activity-logs__tabs-section(v-if="!showSelectOrganization")
      el-tabs(
        v-model="activeTransactionTab"
        @tab-change="handleTransactionTabChange"
        class="c-transaction-tabs"
      )
        el-tab-pane(label="Sticker Transactions" name="sticker")
        el-tab-pane(label="Stripe Transactions" name="stripe")
        el-tab-pane(label="Benevity Transactions" name="benevity")
        el-tab-pane(label="Benevity W2W Credit Transactions" name="benevity-w2w")

    SelectOrganizationWarning(v-if="showSelectOrganization" type="logs")

    //- Main Content Card
    section.c-activity-content-card(v-if="!showSelectOrganization")
      //- Filters
      article.c-activity-content-card__filters
        ActivityTableFilters

    //- Table (outside the card to allow date range card to be separate)
    section.c-activity-table-section(v-if="!showSelectOrganization")
      ActivityTable
</template>

<style lang="scss" scoped>
// Color Variables - Match Dashboard.vue
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-300: #d1d5db;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

.c-activity-logs {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 2rem;
    margin-bottom: 0;
  }

  &__header-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__heading {
    font-size: 1.875rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: $gray-600;
    margin: 0;
    line-height: 1.5;
  }

  &__export-btn {
    height: 36px;
    width: 110px;
    font-size: 14px;
    border-radius: 8px;
    background-color: $color-purple-text !important;
    border-color: $color-purple-text !important;

    &:hover,
    &:focus {
      background-color: color.adjust($color-purple-text, $lightness: -8%) !important;
      border-color: color.adjust($color-purple-text, $lightness: -8%) !important;
    }

    :deep(span) {
      font-weight: 500;
    }
  }

  &__date-section {
    margin-top: 0;
    margin-bottom: 0;
  }

  &__nav-section {
    margin-top: 0;
    margin-bottom: 0;
  }

  &__nav-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
  }

  &__nav-actions {
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
  }

  &__tabs-section {
    margin-top: 0;
    margin-bottom: 0;
  }
}

// Navigation Tabs (Pill Style) - Match Directory/Stickers page
.c-activity-nav {
  display: inline-flex;
  height: 44px;
  align-items: center;
  justify-content: center;
  background-color: $gray-100;
  border-radius: 12px;
  padding: 4px;
  gap: 0;
  flex-wrap: wrap;
  overflow-x: auto;
}

.c-activity-nav-link {
  display: inline-flex;
  height: calc(100% - 2px);
  align-items: center;
  justify-content: center;
  padding: 6px 24px;
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 600;
  color: $gray-600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  user-select: none;
  background: none;
  border: none;

  &:hover:not(:disabled) {
    color: $gray-900;
    background-color: rgba(255, 255, 255, 0.5);
  }

  &--active {
    background-color: white;
    color: $gray-900;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    cursor: not-allowed;
  }
}

// Main Content Card - Match Dashboard.vue section-card style
.c-activity-content-card {
  background: transparent;
  border-radius: 0;
  border: none;
  box-shadow: none;
  overflow: visible;

  &__filters {
    padding: 0;
    border-bottom: none;
  }
}

.c-activity-table-section {
  background: white;
  border-radius: 16px;
  border: 1px solid #dfdfdf;
  box-shadow: none;
  overflow: hidden;

  // Enable horizontal scrolling
  :deep(.el-table) {
    min-width: 100%;
  }

  :deep(.el-table__body-wrapper) {
    overflow-x: auto;
  }
}

.c-activity-action-link {
  font-size: 0.875rem;
  font-weight: 600;
  color: $color-teal-accent;
  transition: color 0.2s;

  &:hover {
    color: #01a89c;
  }
}

// Transaction Tabs Styling - Match Dashboard.vue
.c-transaction-tabs {
  background: white;
  border-radius: 16px;
  border: 1px solid #dfdfdf;
  box-shadow: none;
  padding: 0.75rem 1rem;

  :deep(.el-tabs__header) {
    margin: 0;
    border-bottom: none;
  }

  :deep(.el-tabs__nav-wrap) {
    &::after {
      height: 0;
    }
  }

  :deep(.el-tabs__item) {
    padding: 0 1rem;
    height: 40px;
    line-height: 40px;
    font-size: 0.8125rem;
    font-weight: 500;
    color: $gray-500;
    border: none;

    &.is-active {
      color: $color-purple-text;
      font-weight: 600;
    }

    &:hover {
      color: $color-purple-text;
    }
  }

  :deep(.el-tabs__active-bar) {
    height: 2px;
    background-color: $color-purple-text;
  }

  :deep(.el-tabs__nav) {
    border: none;
  }
}

// Checkbox styling
:deep(.el-checkbox__label) {
  display: none;
}

// Responsive Design - Match Dashboard.vue breakpoints
@media (max-width: 768px) {
  .c-activity-logs {
    padding: 1.5rem;

    &__header {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
    }

    &__heading {
      font-size: 1.5rem;
    }

    &__subtitle {
      font-size: 0.8125rem;
    }

    &__export-btn {
      width: 100%;
    }
  }

  .c-activity-logs {
    &__nav-wrapper {
      flex-direction: column;
      align-items: flex-start;
    }

    &__nav-actions {
      width: 100%;
    }
  }

  .c-activity-nav {
    width: 100%;
    height: auto;
    flex-wrap: wrap;
    padding: 4px;
  }

  .c-activity-nav-link {
    font-size: 0.875rem;
    padding: 6px 16px;
    min-width: fit-content;
  }

  .c-activity-content-card {
    &__filters {
      padding: 1rem;
    }
  }
}

@media (max-width: 480px) {
  .c-activity-logs {
    padding: 1rem;

    &__heading {
      font-size: 1.25rem;
    }

    &__subtitle {
      font-size: 0.75rem;
    }
  }
}
</style>
